import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Icon,
  Badge,
  Flex,
  Button,
  Skeleton,
  Center,
} from '@chakra-ui/react'
import { useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useLabAppAccess } from '../../hooks/useLabAppAccess'
import { LAB_APPS, labAppOfBlotModule, labAppPath, type LabApp } from '../../config/labApps'
import { moduleOfPathologyId } from '../Lab/BlotAnalysisWorkspace/ImmunoBlotSelection'
import { FaMicroscope, FaXRay, FaUsers } from 'react-icons/fa'
import { LuActivity, LuVideo, LuClock, LuHistory } from 'react-icons/lu'
import { formatDistanceToNow } from 'date-fns'
import { enUS, fr } from 'date-fns/locale'
import axiosClient from '../../api/axios'
import type { MalariaAnalysisCase } from '../../api/api_hooks/lab/malariaAnalyses'
import { pathologyDisplayName } from '../Lab/BlotAnalysisWorkspace/utils/displayName'

// Activity type definition
export interface Activity {
  id: string
  type: 'malaria_analysis' | 'blot_analysis' | 'radiology_analysis' | 'teleexpertise_created' | 'teleexpertise_shared' | 'patient_added'
  timestamp: Date
  resourceId?: string | number
  details?: {
    patientName?: string
    fileName?: string
    count?: number
    parasitemia?: number | null
    totalCells?: number
    pathologyName?: string | null
    pathologyId?: string | null
    totalBlots?: number
    validatedBlots?: number
  }
}

/** Where a blot case stands: every strip given a verdict, some, or none. */
const validationState = (details: Activity['details']) => {
  const total = details?.totalBlots ?? 0
  const done = details?.validatedBlots ?? 0
  if (total === 0) return null
  if (done >= total) return { label: 'home.recentActivity.validated', scheme: 'green', params: {} }
  if (done > 0) return { label: 'home.recentActivity.partlyValidated', scheme: 'orange', params: { done, total } }
  return { label: 'home.recentActivity.toValidate', scheme: 'gray', params: {} }
}

// Icon mapping for each activity type
const ACTIVITY_ICONS: Record<Activity['type'], React.ElementType> = {
  malaria_analysis: FaMicroscope,
  blot_analysis: LuActivity,
  radiology_analysis: FaXRay,
  teleexpertise_created: LuVideo,
  teleexpertise_shared: LuVideo,
  patient_added: FaUsers,
}

// Color mapping for each activity type
const ACTIVITY_COLORS: Record<Activity['type'], { bg: string; icon: string; badge: string }> = {
  malaria_analysis: { bg: 'purple.50', icon: 'purple.500', badge: 'purple' },
  blot_analysis: { bg: 'teal.50', icon: 'teal.500', badge: 'teal' },
  radiology_analysis: { bg: 'blue.50', icon: 'blue.500', badge: 'blue' },
  teleexpertise_created: { bg: 'green.50', icon: 'green.500', badge: 'green' },
  teleexpertise_shared: { bg: 'green.50', icon: 'green.500', badge: 'green' },
  patient_added: { bg: 'orange.50', icon: 'orange.500', badge: 'orange' },
}

// Le filtre porte les identifiants d'applications du registre, plus « tout ».
type FilterType = 'all' | string

/**
 * À quelle application du Lab Studio appartient une activité. Les analyses
 * paludisme sont à ALMA ; un cas Western blot appartient à l'application qui
 * possède son test (IRIS par défaut, PROTIS pour les siens) — même table en
 * base, seul le pathologyId les distingue. null : l'activité n'est pas une
 * application du Lab (téléexpertise…), elle ne se filtre pas par ce biais.
 */
const activityApp = (a: Activity): LabApp | null => {
  if (a.type === 'malaria_analysis') return LAB_APPS.find((app) => app.id === 'parasitology') ?? null
  if (a.type === 'blot_analysis') return labAppOfBlotModule(moduleOfPathologyId(a.details?.pathologyId))
  return null
}

// Helper function to get route path based on activity type
const getActivityRoute = (activity: Activity): string => {
  const { type, resourceId } = activity

  switch (type) {
    case 'malaria_analysis':
      return resourceId ? `/lab/parasitology/${resourceId}` : '/lab/parasitology'
    case 'blot_analysis': {
      // Un cas PROTIS s'ouvre dans PROTIS, pas sous la bannière d'IRIS.
      const base = labAppPath(activityApp(activity)?.id ?? 'blot-analysis')
      return resourceId ? `${base}/${resourceId}` : base
    }
    case 'radiology_analysis':
      return resourceId ? `/lab/radiology/${resourceId}` : '/lab/radiology'
    case 'teleexpertise_created':
    case 'teleexpertise_shared':
      return resourceId ? `/myCollaboratory/${resourceId}` : '/myCollaboratory'
    case 'patient_added':
      return resourceId ? `/patients/${resourceId}` : '/patients'
    default:
      return '/home'
  }
}

export default function RecentActivity() {
  // Une application coupée dans l'administration sort aussi du fil : ni ses
  // cas, ni sa pastille de filtre. Voir ses analyses ALMA passées alors que
  // le module est fermé inviterait à les rouvrir — vers une porte close.
  const { isAppEnabled } = useLabAppAccess()

  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [filter, setFilter] = useState<FilterType>('all')
  const [isLoading, setIsLoading] = useState(true)
  const [activities, setActivities] = useState<Activity[]>([])
  const [error, setError] = useState<string | null>(null)

  // Fetch recent activities from the backend
  const fetchActivities = useCallback(async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Get auth data from localStorage (same way as axios interceptor)
        const authData = JSON.parse(localStorage.getItem('auth') || '{}')
        const token = authData.accessToken

        if (!token) {
            setIsLoading(false)
          return
        }

        const headers = {
          Authorization: token,
          'Content-Type': 'application/json',
        }

        const [recentRes, labCasesRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_DOMAIN}/api/lab-analysis/recent-activities`, {
            headers,
          }),
          axiosClient
            .get<{ success: boolean; cases: MalariaAnalysisCase[] }>('/analysis/malaria/lab-cases')
            .catch((err) => {
              console.warn('Malaria lab-cases fetch failed', err?.response?.status)
              return { data: { success: false, cases: [] } }
            }),
        ])

        if (!recentRes.ok) {
          const errorText = await recentRes.text()
          console.error('API Error Response:', errorText)
          throw new Error(`Failed to fetch activities: ${recentRes.status} ${recentRes.statusText}`)
        }

        const recentData = await recentRes.json()
        const malariaCases = labCasesRes?.data?.cases || []

        if (!recentData.activities || !Array.isArray(recentData.activities)) {
          console.error('Invalid response format:', recentData)
          throw new Error('Invalid response format - expected activities array')
        }

        const parsedRecent = recentData.activities.map((activity: any) => ({
          ...activity,
          timestamp: new Date(activity.timestamp),
        }))

        // Map malaria cases from lab-cases endpoint (covers ALMA / parasitology runs)
        const malariaActivities: Activity[] = Array.isArray(malariaCases)
          ? malariaCases.flatMap((c: MalariaAnalysisCase) =>
              (c.images || []).map((img) => ({
                id: `malaria_${c.id}_${img.id}`,
                type: 'malaria_analysis' as Activity['type'],
                timestamp: new Date(img.createdAt || c.date || Date.now()),
                resourceId: c.id,
                details: {
                  fileName: c.slideId || img.imageUrl || '',
                  count: img.infectedRbcCount || 0,
                },
              }))
            )
          : []

        // Merge and de-duplicate by id. Patient-added events are excluded — the
        // home activity feed is about analyses (IRIS / ALMA), not the address book.
        const merged = [...parsedRecent, ...malariaActivities]
          .filter((act: Activity) => act.type !== 'patient_added')
          .reduce<Activity[]>((acc, act) => {
            if (!acc.some((a: Activity) => a.id === act.id)) {
              acc.push(act as Activity)
            }
            return acc
          }, [])
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())

        setActivities(merged)
      } catch (err) {
        console.error('Error fetching recent activities:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
        setActivities([])
      } finally {
        setIsLoading(false)
      }
  }, [])

  // Re-read on mount AND whenever the tab regains focus: validating a case
  // happens on another screen, so a badge fetched once at mount kept showing
  // the old count ("3/4 validés" on a case the biologist had just finished).
  useEffect(() => {
    fetchActivities()
    const refresh = () => {
      if (document.visibilityState === 'visible') fetchActivities()
    }
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [fetchActivities])

  const filteredActivities = useMemo(() => {
    const allowed = activities.filter((a) => {
      const app = activityApp(a)
      return !app || isAppEnabled(app.id)
    })
    if (filter === 'all') return allowed
    return allowed.filter((a) => activityApp(a)?.id === filter)
  }, [activities, filter, isAppEnabled])

  // Format timestamp with i18n support
  const formatTimestamp = (date: Date) => {
    const locale = i18n.language === 'fr' ? fr : enUS
    return formatDistanceToNow(date, { addSuffix: true, locale })
  }

  return (
    <VStack align="stretch" spacing={5} height="100%" overflow="hidden">
      {/* Header */}
      <Flex
        alignItems={{ base: 'flex-start', md: 'center' }}
        flexShrink={0}
        justify="space-between"
        width="100%"
        direction={{ base: 'column', md: 'row' }}
        gap={4}
      >
        <HStack spacing={3}>
          <Box
            w="44px"
            h="44px"
            borderRadius="2xl"
            bgGradient="linear(135deg, #6366f1, #8b5cf6)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxShadow="0 8px 20px -4px rgba(139,92,246,0.40)"
            position="relative"
          >
            <Icon as={LuHistory} color="white" boxSize="20px" />
            <Box
              position="absolute"
              inset="0"
              borderRadius="2xl"
              bgGradient="linear(135deg, rgba(255,255,255,0.25), transparent 50%)"
              pointerEvents="none"
            />
          </Box>
          <Box>
            <Heading size="md" color="gray.800" letterSpacing="-0.01em">
              {t('home.recentActivity.title')}
            </Heading>
            <Text fontSize="xs" color="gray.500" mt={0.5}>
              {filteredActivities.length} action{filteredActivities.length > 1 ? 's' : ''} récente{filteredActivities.length > 1 ? 's' : ''}
            </Text>
          </Box>
        </HStack>

        {/* Filter pills — colour-coded per module */}
        <HStack spacing={1.5} flexWrap="wrap">
          {([
            { key: 'all', label: 'Tout', scheme: 'gray', gradient: 'linear(135deg, #6b7280, #4b5563)' },
            ...LAB_APPS.filter((app) => isAppEnabled(app.id)).map((app) => ({
              key: app.id,
              label: app.label,
              scheme: app.colorScheme,
              gradient: `linear(135deg, ${app.accentColor}, ${app.accentColor})`,
            })),
          ] as { key: FilterType; label: string; scheme: string; gradient: string }[]).map(({ key, label, scheme, gradient }) => {
            const active = filter === key
            return (
              <Button
                key={key}
                size="sm"
                borderRadius="full"
                onClick={() => setFilter(key)}
                bgGradient={active ? gradient : undefined}
                bg={active ? undefined : 'transparent'}
                color={active ? 'white' : `${scheme}.600`}
                border="1px solid"
                borderColor={active ? 'transparent' : `${scheme}.100`}
                fontSize="xs"
                fontWeight="bold"
                px={3.5}
                h="32px"
                boxShadow={active ? `0 4px 10px rgba(0,0,0,0.10)` : 'none'}
                _hover={{
                  bg: active ? undefined : `${scheme}.50`,
                  transform: 'translateY(-1px)',
                }}
                transition="all 0.18s"
              >
                {label}
              </Button>
            )
          })}
        </HStack>
      </Flex>

      {/* Activity list — custom-styled scrollbar, padded so card hover shadows
          and the -2 px lift never get clipped by the overflow boundary. */}
      <Box
        flex={1}
        overflowY="auto"
        overflowX="hidden"
        maxH="500px"
        pr={2}
        pl={1}
        py={1}
        sx={{
          // Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(148, 163, 184, 0.45) transparent',
          // WebKit
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            margin: '4px 0',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(180deg, rgba(168, 85, 247, 0.35), rgba(20, 184, 166, 0.35))',
            borderRadius: '999px',
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
            transition: 'background 0.18s ease',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'linear-gradient(180deg, rgba(168, 85, 247, 0.65), rgba(20, 184, 166, 0.65))',
            backgroundClip: 'padding-box',
            border: '2px solid transparent',
          },
        }}>
        {isLoading ? (
          <VStack align="stretch" spacing={4}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height="80px" borderRadius="lg" />
            ))}
          </VStack>
        ) : error ? (
          <Center h="200px">
            <VStack spacing={3}>
              <Icon as={LuClock} boxSize={12} color="red.400" />
              <Text color="red.500" fontSize="lg" fontWeight="semibold">
                {t('home.recentActivity.error') || 'Error loading activities'}
              </Text>
              <Text color="gray.500" fontSize="sm">
                {error}
              </Text>
            </VStack>
          </Center>
        ) : filteredActivities.length === 0 ? (
          <Center h="200px" bg="gray.50" borderRadius="xl" border="1px dashed" borderColor="gray.200">
            <VStack spacing={3}>
              <Box
                w={14}
                h={14}
                borderRadius="full"
                bgGradient="linear(135deg, gray.100, gray.200)"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={LuClock} boxSize={6} color="gray.400" />
              </Box>
              <Text color="gray.600" fontSize="md" fontWeight="semibold">
                {t('home.recentActivity.noActivity')}
              </Text>
              {activities.length > 0 && (
                <Text color="gray.500" fontSize="xs">
                  {t('home.recentActivity.noActivityFiltered') || 'Aucune activité ne correspond au filtre sélectionné'}
                </Text>
              )}
            </VStack>
          </Center>
        ) : (
          <VStack align="stretch" spacing={3}>
            {filteredActivities.map((activity) => {
              const app = activityApp(activity)
              // Les activités des applications du Lab portent les couleurs et
              // le nom de LEUR application (PROTIS n'est pas IRIS) ; le reste
              // garde sa table par type.
              const colors = app
                ? { bg: `${app.colorScheme}.50`, icon: `${app.colorScheme}.500`, badge: app.colorScheme }
                : ACTIVITY_COLORS[activity.type]
              const ActivityIcon = app ? (app.icon as React.ElementType) : ACTIVITY_ICONS[activity.type]

              const badgeLabel = app ? app.label
                : activity.type === 'patient_added' ? 'Patient'
                : activity.type === 'teleexpertise_created' || activity.type === 'teleexpertise_shared' ? t('home.recentActivity.badges.teleexpertise')
                : t('home.recentActivity.badges.analysis')

              return (
                <Box
                  key={activity.id}
                  bg="white"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.100"
                  cursor="pointer"
                  transition="all 0.22s cubic-bezier(0.4, 0, 0.2, 1)"
                  onClick={() => navigate(getActivityRoute(activity))}
                  overflow="hidden"
                  position="relative"
                  role="group"
                  _hover={{
                    borderColor: colors.icon,
                    boxShadow: `0 10px 24px -12px ${colors.icon === 'teal.500' ? 'rgba(20,184,166,0.30)'
                      : colors.icon === 'cyan.500' ? 'rgba(6,182,212,0.30)'
                      : colors.icon === 'purple.500' ? 'rgba(168,85,247,0.30)'
                      : colors.icon === 'blue.500' ? 'rgba(59,130,246,0.30)'
                      : colors.icon === 'green.500' ? 'rgba(34,197,94,0.30)'
                      : 'rgba(249,115,22,0.30)'}`,
                    transform: 'translateY(-2px)',
                  }}
                >
                  {/* Decorative gradient strip on the left edge */}
                  <Box
                    position="absolute"
                    top={0}
                    bottom={0}
                    left={0}
                    w="4px"
                    bgGradient={`linear(to-b, ${colors.icon}, ${colors.icon})`}
                    opacity={0.85}
                  />
                  <HStack spacing={0} align="stretch" pl="6px">
                    <HStack spacing={3} p={3.5} flex={1} align="center">
                      {/* Icon */}
                      <Box
                        bg={colors.bg}
                        borderRadius="xl"
                        p={2.5}
                        flexShrink={0}
                        transition="transform 0.2s"
                        _groupHover={{ transform: 'scale(1.08)' }}
                      >
                        <Icon as={ActivityIcon} boxSize={4} color={colors.icon} />
                      </Box>

                      {/* Content */}
                      <VStack align="flex-start" spacing={0.5} flex={1} minW={0}>
                        <HStack spacing={2} w="full" justify="space-between">
                          {/* Name the test itself ("Aspergillus WB IgG") rather than
                              the generic action: on a feed where nearly every row is
                              an immunoblot, the module name says nothing. Falls back
                              to the generic label when the pathology is unknown. */}
                          <Text fontWeight="700" fontSize="sm" color="gray.800" noOfLines={1}>
                            {activity.type === 'blot_analysis' && activity.details?.pathologyName
                              ? pathologyDisplayName(activity.details.pathologyName)
                              : t(`home.recentActivity.actions.${activity.type}`)}
                          </Text>
                          <HStack spacing={1.5} flexShrink={0}>
                            {(() => {
                              if (activity.type !== 'blot_analysis') return null
                              const state = validationState(activity.details)
                              if (!state) return null
                              return (
                                <Badge
                                  colorScheme={state.scheme}
                                  variant="subtle"
                                  fontSize="2xs"
                                  borderRadius="full"
                                  px={2}
                                  fontWeight="bold"
                                >
                                  {t(state.label, state.params)}
                                </Badge>
                              )
                            })()}
                            <Badge
                              colorScheme={colors.badge}
                              fontSize="2xs"
                              borderRadius="full"
                              px={2}
                              fontWeight="bold"
                            >
                              {badgeLabel}
                            </Badge>
                          </HStack>
                        </HStack>

                        {(() => {
                          const { type, details } = activity
                          const parts: string[] = []

                          if (type === 'blot_analysis') {
                            // The pathology moved up to the title, so the subtitle
                            // carries the size of the run instead.
                            if (details?.totalBlots) {
                              parts.push(`${details.totalBlots} bandelette${details.totalBlots > 1 ? 's' : ''}`)
                            }
                            if (details?.count) parts.push(`${details.count} bandes`)
                          } else if (type === 'malaria_analysis') {
                            const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(details?.fileName || '')
                            if (!isUUID && details?.fileName) parts.push(details.fileName)
                            if (details?.parasitemia != null) {
                              parts.push(`Parasitémie ${details.parasitemia.toFixed(1)}%`)
                            } else if (details?.count && details.totalCells) {
                              const pct = ((details.count / details.totalCells) * 100).toFixed(1)
                              parts.push(`Parasitémie ${pct}%`)
                            }
                          } else if (details?.patientName) {
                            parts.push(details.patientName)
                          }

                          if (parts.length === 0) return null
                          return (
                            <Text fontSize="xs" color="gray.400" noOfLines={1}>
                              {parts.join(' · ')}
                            </Text>
                          )
                        })()}

                        <HStack spacing={1.5} fontSize="2xs" color="gray.400" mt={0.5}>
                          <Icon as={LuClock} boxSize={2.5} />
                          <Text>{formatTimestamp(activity.timestamp)}</Text>
                          <Text color="gray.300">·</Text>
                          <Text>
                            {activity.timestamp.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                              day: 'numeric', month: 'short', year: 'numeric'
                            })}
                          </Text>
                        </HStack>
                      </VStack>
                    </HStack>
                  </HStack>
                </Box>
              )
            })}
          </VStack>
        )}
      </Box>
    </VStack>
  )
}
